import React, { useCallback, useEffect } from "react";

import MicrosoftTodoLogo from "~/integrations/microsoftTodo/MicrosoftTodoLogo";
import { plural } from "~/lib/utils";
import { Button } from "~/ui";
import Checkbox from "~/ui/legacy/Checkbox";
import { Modal } from "~/ui/legacyFellowIndex";
import Selector, { type SelectorOption, type SingleValue } from "~/ui/selectors/Selector";

import type { AddOrChangeListResponseProps, MicrosoftTodoListsListType } from "./MicrosoftTodoIntegration";

import styles from "./MicrosoftTodo.module.css";

type MicrosoftTodoAddListModalProps = {
	todoLists: MicrosoftTodoListsListType;
	actionItemCount: number;
	onClose: (result: AddOrChangeListResponseProps | undefined) => void;
	defaultList: SelectorOption | null;
};

export default function MicrosoftTodoAddListModal({
	todoLists,
	actionItemCount,
	onClose,
	defaultList,
}: MicrosoftTodoAddListModalProps) {
	const [syncEnabled, setSyncEnabled] = React.useState(false);
	const [selectedList, setSelectedList] = React.useState<SingleValue<SelectorOption>>(defaultList);
	const [todoListOptions, setTodoListOptions] = React.useState<SelectorOption[]>([]);

	useEffect(() => {
		setTodoListOptions(
			todoLists.map(list => ({
				value: list.externalId,
				label: list.name,
			})),
		);
	}, [todoLists]);

	const onCancel = useCallback(() => {
		onClose(undefined);
	}, [onClose]);

	const onAddList = useCallback(() => {
		if (!selectedList) return;
		onClose({
			listId: selectedList.value,
			listName: selectedList.label,
			syncEnabled: true,
			triggerManualSync: syncEnabled,
		});
	}, [onClose, selectedList, syncEnabled]);

	const onSyncEnabledChange = useCallback(() => {
		setSyncEnabled(!syncEnabled);
	}, [syncEnabled]);

	const s = actionItemCount ? plural(actionItemCount) : "";

	return (
		<Modal onClose={onCancel} className={styles.addListModal}>
			<Modal.Header onClose={onCancel}>
				<Modal.Title>
					<MicrosoftTodoLogo className={styles.logo} /> Select a Microsoft To Do List
				</Modal.Title>
			</Modal.Header>
			<Modal.Body dark className={styles.addListBody}>
				<Selector
					value={selectedList}
					options={todoListOptions}
					onChange={setSelectedList}
					placeholder="Select a list"
				/>
				{actionItemCount != 0 && (
					<div className={styles.actionItemCount}>
						<Checkbox green checked={syncEnabled} onChange={onSyncEnabledChange} className={styles.checkIcon} />
						<span>
							Create new To Do task{s} for <b>{actionItemCount}</b> incomplete action item{s} in Fellow.
						</span>
					</div>
				)}
			</Modal.Body>
			<Modal.Footer>
				<Button size="normal" onClick={onCancel}>
					Cancel
				</Button>
				<Button size="normal" variant="primary" disabled={selectedList === null} onClick={onAddList}>
					Add
				</Button>
			</Modal.Footer>
		</Modal>
	);
}
